<template>
  <div
    ref="baseChart"
    class="base-chart"
  >
    <div class="base-chart--toolbar">
      <slot
        v-if="currentOptions.chart.toolbar.show"
        name="toolbar"
      >
        <div class="base-chart--toolbar-item">
          <div
            v-if="isResolutionActive"
            class="flex gap-3 items-center"
          >
            <b>Ansicht</b>
            <BaseSelect
              v-model="currentResolution"
              :items="currentResolutions"
              :clearable="false"
              outlined
              rounded
              x-small
            />
          </div>
          <div
            v-if="isTimeRangeActive"
            class="base-chart--time-range"
          >
            <span style="grid-area: timeRangeLabel">Zeitraum</span>
            <BaseDateTimePicker
              v-if="!isTimeRangeSelect"
              v-model="currentTimeRange"
              range
              auto-prefill
              outlined
              rounded
              x-small
            />
            <BaseSelect
              v-if="isTimeRangeSelect"
              v-model="selectedTimeRange"
              style="grid-area: timeRangeSelect"
              :items="currentOptions.chart.toolbar.timeRange.items"
              :shadow-items="currentOptions.chart.toolbar.timeRange.shadowItems"
              :clearable="false"
              outlined
              rounded
              x-small
            />
            <div
              v-if="isTimeRangeSelect"
              class="base-chart--toolbar-datepicker"
              style="grid-area: timeRangeDatePicker"
            >
              <BaseButtonV2
                v-if="customTimeRangeType !== 'custom'"
                :disabled="isMinDateReached"
                type="icon"
                color="text"
                @click="prevTimeRange"
              >
                <BaseIcon icon="fal fa-chevron-left" />
              </BaseButtonV2>
              <BaseDateTimePicker
                :key="`${selectedTimeRange}-${customTimeRangeType === 'custom' ? customTimeRangeType : `${customTimeRangeType}-${currentTimeRange}`}`"
                :value="pickedTimeRange"
                :range="selectedTimeRange === 'custom' || !isTimeRangeSelect"
                :max-range="currentOptions.chart.toolbar.timeRange.maxRange"
                :type="customTimeRangeType === 'custom' ? 'day' : customTimeRangeType"
                auto-prefill
                :clearable="false"
                :icon-right="null"
                small
                :min="calculateTimeRange[0]"
                :max="calculateTimeRange[1]"
                @update="updatePickedRange($event)"
              />
              <BaseButtonV2
                v-if="customTimeRangeType !== 'custom'"
                :disabled="isMaxDateReached"
                type="icon"
                color="text"
                @click="nextTimeRange"
              >
                <BaseIcon icon="fal fa-chevron-right" />
              </BaseButtonV2>
            </div>
          </div>
        </div>
        <div class="base-chart--toolbar-item">
          <BaseButtonV2
            type="fab"
            color="text"
            :active="currentType === 'column'"
            small
            aria-label="Chart Type: Spalten-Diagramm"
            @click="currentType = 'column'"
          >
            <BaseIcon
              icon="fal fa-chart-bar"
              :style="{ fontSize: '16px' }"
            />
          </BaseButtonV2>
          <BaseButtonV2
            type="fab"
            color="text"
            :active="currentType === 'area'"
            small
            aria-label="Chart Type: Flächen-Diagramm"
            @click="currentType = 'area'"
          >
            <BaseIcon
              icon="fal fa-chart-area"
              :style="{ fontSize: '16px' }"
            />
          </BaseButtonV2>
          <BaseButtonV2
            type="fab"
            color="text"
            :active="currentType === 'line'"
            small
            aria-label="Chart Type: Linien-Diagramm"
            @click="currentType = 'line'"
          >
            <BaseIcon
              icon="fal fa-chart-line"
              :style="{ fontSize: '16px' }"
            />
          </BaseButtonV2>
        </div>
      </slot>
    </div>
    <component
      :is="component"
      v-if="showChart && canBeMounted"
      :key="chartId"
      :data="currentData"
      :height="height"
      :width="width"
      :is-loading="isLoading"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { merge } from 'lodash';
import axios from 'axios';
import Observer from '@/classes/DomObserver';
import Delay from '@/helper/Delay';

import { OPTIONS } from '../../utils/BaseChartDefaults';
import Chart from '@/classes/Chart';

export default {
  name: 'BaseChart',
  props: {
    endpoint: {
      type: String,
      default: '',
    },
    params: {
      type: Object,
      default: () => ({}),
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    series: {
      type: Array,
      default: () => ([]),
    },
    height: {
      type: [String, Number],
      default: 'auto',
    },
    width: {
      type: [String, Number],
      default: '100%',
    },
    chartType: {
      type: String,
      default: 'high',
    },
    delay: {
      type: Number,
      default: 2000,
    },
  },
  setup(props) {
    const that = this;
    return {
      component: window.Vue.component('VueHighChart', () => import('./HighChart.vue')),
      OPTIONS,
      xAxisObserver: new Observer((mutation) => {
        that?.setVariable('visibleLabels', mutation.target.childElementCount);
      }, 'mutation'),
      canBeMounted: props.endpoint === '' ? props.series.length : false,
      updateDelay: new Delay(props.delay),
      chartClass: null,
    };
  },
  data() {
    const vars = {
      visibleLabels: null,
      currentZoom: null,
    };

    let isZooming = false;
    const that = this;
    const events = {
      chart: {
        events: {
          selection: (event) => {
            const isReset = !event.xAxis;

            if (isReset) {
              this.setVariable('currentZoom', null);
            } else {
              this.setVariable('currentZoom', event.xAxis.map((series) => ({
                min: series.min,
                max: series.max,
              })));
            }

            isZooming = true;
          },
          render: (event) => {
            if (isZooming) {
              isZooming = false;
              return;
            }

            if (this.xAxisObserver) {
              this.xAxisObserver.observe(document.querySelector('.base-chart'));
            }
          },
          redraw(event) {
            that.setVariable('chartClass', event.target.options);
            that.setTooltip();
            that.$emit('chart', event.target.options);
          },
        },
      },
    };

    if (this.endpoint) {
      OPTIONS.series = [];
    }

    const options = merge(OPTIONS, this.options, events);
    return {
      chartId: this.IOH.randomId(),
      currentData: null,
      currentOptions: options,
      currentType: options.chart.type,
      currentResolution: options.chart.toolbar.resolution.value,
      currentTimeRange: options.chart.toolbar.timeRange.value,
      pickedTimeRange: options.chart.toolbar.timeRange.value,
      currentResolutions: options.chart.toolbar.resolution.list,
      currentParams: this.params,
      isMounted: false,
      isLoading: false,
      selectedTimeRange: options.chart.toolbar.timeRange.default,
      ...vars,
    };
  },
  computed: {
    showChart() {
      return this.currentData && !!Object.keys(this.currentData).length;
    },
    isToolbarActive() {
      return this.currentOptions?.chart?.toolbar?.show;
    },
    isTimeRangeActive() {
      return this.currentOptions?.chart?.toolbar?.timeRange?.show;
    },
    isTimeRangeSelect() {
      return this.currentOptions?.chart?.toolbar?.timeRange?.type === 'select';
    },
    isResolutionActive() {
      return this.currentOptions?.chart?.toolbar?.resolution?.show;
    },
    customTimeRangeType() {
      if (!this.isTimeRangeSelect || ['tomorrow', 'today', 'day'].includes(this.selectedTimeRange)) {
        return 'day';
      }
      if (['currentMonth', 'lastMonth', 'month'].includes(this.selectedTimeRange)) {
        return 'month';
      }
      return 'custom';
    },
    timeRangeGridColumns() {
      return this.isTimeRangeSelect ? 'auto auto auto' : 'auto auto';
    },
    calculateTimeRange() {
      const {
        min = new Date().subYears(100),
        max = null,
      } = this.currentOptions.chart.toolbar.timeRange.limits || {};
      return [min, max];
    },
    isMinDateReached() {
      if (!this.currentTimeRange?.[0] || !this.calculateTimeRange[0]) return false;
      return this.normalizeDate(this.currentTimeRange[0]) === this.normalizeDate(this.calculateTimeRange[0]);
    },
    isMaxDateReached() {
      if (!this.currentTimeRange?.[0] || !this.calculateTimeRange[1]) return false;
      if (this.customTimeRangeType === 'day') {
        return this.normalizeDate(this.currentTimeRange[0]) === this.normalizeDate(new Date().addDays(1));
      }
      if (this.customTimeRangeType === 'month') {
        return this.normalizeDate(new Date(this.currentTimeRange[0]).setDate(1)) === this.normalizeDate(new Date().setDate(1));
      }
      return this.normalizeDate(this.currentTimeRange[0]) === this.normalizeDate(this.calculateTimeRange[1]);
    },
  },
  watch: {
    params: {
      deep: true,
      handler(val) {
        this.currentParams = { ...this.currentParams, ...val };
        this.$emit('params', this.currentParams);
        this.updateDelay.start(() => {
          this.update();
        });
      },
    },
    options: {
      deep: true,
      handler(val) {
        this.currentOptions = merge(this.currentOptions, val);
        this.$emit('options', this.currentOptions);
        this.currentResolutions = this.getResolutions();
      },
    },
    endpoint() {
      this.updateDelay.start(() => {
        this.update();
      });
    },
    visibleLabels(val) {
      this.currentOptions = merge(this.currentOptions, {
        xAxis: {
          labels: {
            // eslint-disable-next-line no-nested-ternary
            step: val < 7 ? 1 : val < 21 ? 2 : 3,
          },
        },
      });

      this.chartId = this.IOH.randomId();
      this.$emit('options', this.currentOptions);
    },
    currentZoom(val) {
      const { min, max } = val && val[0] ? val[0] : { min: null, max: null };
      this.currentOptions = merge(this.currentOptions, { xAxis: { min, max } });

      this.$emit('options', this.currentOptions);
    },
    currentResolutions(val, prev) {
      if (val !== prev && this.isResolutionActive && this.isMounted) {
        if (!val.find((item) => item.value === this.currentResolution)) {
          this.currentResolution = val[val.length - 1].value;
        }
        this.$emit('resolutions', val);
        this.updateDelay.start(() => {
          this.update();
        });
      }
    },
    currentResolution(val, prev) {
      if (val !== prev && this.isResolutionActive && this.isMounted) {
        this.$emit('resolution', val);
        this.updateDelay.start(() => {
          this.update();
        });
      }
    },
    currentTimeRange(val, prev) {
      if (val !== prev && this.isTimeRangeActive && this.isMounted) {
        if (this.isTimeRangeSelect) {
          this.setTimeRangeValue(this.currentTimeRange, this.customTimeRangeType);
        }
        this.$emit('timeRange', val);
        this.updateDelay.start(() => {
          this.update();
        });
      }
    },
    selectedTimeRange: {
      immediate: true,
      handler(val, prev) {
        this.selectTimeRange(val, prev);
      },
    },
    currentType(val, prev) {
      if (val !== prev && this.isMounted) {
        this.$emit('type', val);
      }

      this.currentOptions = merge(this.currentOptions, { chart: { type: val } });
    },
  },
  mounted() {
    this.isLoading = true;

    const { resolution } = this.options?.chart?.toolbar || {};
    const resolutionCustom = resolution?.value || null;

    const resolutionList = this.getResolutions() || [];
    const resolutionLastItem = resolutionList?.[resolutionList.length - 1]?.value || null;

    const resolutionListDefault = this.OPTIONS?.chart?.toolbar?.resolution?.list || [];
    const resolutionDefault = resolutionListDefault?.[resolutionListDefault.length - 1]?.value || null;

    this.currentResolutions = resolutionList;
    this.currentResolution = resolutionCustom || resolutionLastItem || resolutionDefault;

    this.$nextTick(() => {
      this.isMounted = true;
      this.selectTimeRange(this.selectedTimeRange);
      this.updateDelay.start(() => {
        this.update();
      });
    });
  },
  methods: {
    normalizeDate(date = new Date()) {
      let dateObj = date;
      if (typeof date === 'string' || typeof date === 'number') {
        dateObj = new Date(date);
      }

      if (date instanceof Array) {
        return date.map((item) => {
          let arrDateObj = item;
          if (typeof item === 'string' || typeof item === 'number') {
            arrDateObj = new Date(item);
          }
          arrDateObj = arrDateObj.format('Y-m-d');

          return arrDateObj;
        });
      }

      dateObj = dateObj.format('Y-m-d');

      return dateObj;
    },
    updatePickedRange(val) {
      this.pickedTimeRange = val;
      let value = val;
      if (!(val instanceof Array) || val.length !== 2) {
        if (['day', 'today', 'tomorrow'].includes(this.selectedTimeRange)) {
          value = [
            new Date(val).format('Y-m-d'),
            new Date(val).format('Y-m-d'),
          ];
        }
        if (['month', 'currentMonth', 'lastMonth'].includes(this.selectedTimeRange)) {
          value = [
            new Date(new Date(val).setDate(1)).format('Y-m-d'),
            new Date(new Date(val).addMonths(1).setDate(1)).addDays(-1).format('Y-m-d'),
          ];
        } else {
          value = [
            new Date(val).format('Y-m-d'),
            new Date(val).format('Y-m-d'),
          ];
        }
      }
      this.currentTimeRange = value;
    },
    selectTimeRange(val, prev = null) {
      if (val !== prev && this.isTimeRangeSelect && this.isMounted) {
        if (val === 'today') {
          this.currentTimeRange = [
            this.normalizeDate(new Date().setHours(0, 0, 0, 0)),
            this.normalizeDate(new Date().setHours(23, 59, 59, 999)),
          ];
          [this.pickedTimeRange] = this.currentTimeRange;
        }
        if (val === 'tomorrow') {
          this.currentTimeRange = [
            this.normalizeDate(new Date().addDays(1).setHours(0, 0, 0, 0)),
            this.normalizeDate(new Date().addDays(1).setHours(23, 59, 59, 999)),
          ];
          [this.pickedTimeRange] = this.currentTimeRange;
        }
        if (val === 'currentMonth') {
          this.currentTimeRange = [
            this.normalizeDate(new Date(new Date().setDate(1)).setHours(0, 0, 0, 0)),
            this.normalizeDate(new Date(new Date().addMonths(1).setDate(1)).addDays(-1).setHours(23, 59, 59, 999)),
          ];
          [this.pickedTimeRange] = this.currentTimeRange;
        }
        if (val === 'lastMonth') {
          this.currentTimeRange = [
            this.normalizeDate(new Date(new Date().addMonths(-1).setDate(1)).setHours(0, 0, 0, 0)),
            this.normalizeDate(new Date(new Date().setDate(1)).addDays(-1).setHours(23, 59, 59, 999)),
          ];
          [this.pickedTimeRange] = this.currentTimeRange;
        }
        if (val === 'custom') {
          this.currentTimeRange = [
            this.normalizeDate(),
            this.normalizeDate(),
          ];
          this.pickedTimeRange = this.currentTimeRange;
        }
        this.$emit('selectedTimeRange', val);
        this.updateDelay.start(() => {
          this.update();
        });
      }
    },
    setTimeRangeValue(dates, type) {
      if (type === 'day' && this.normalizeDate(dates[0]) === this.normalizeDate(new Date().addDays(1))) {
        this.selectedTimeRange = 'tomorrow';
      } else if (type === 'day' && this.normalizeDate(dates[0]) === this.normalizeDate()) {
        this.selectedTimeRange = 'today';
      } else if (type === 'month' && this.normalizeDate(dates[0]) === this.normalizeDate(new Date().addMonths(-1).setDate(1))) {
        this.selectedTimeRange = 'lastMonth';
      } else if (type === 'month' && this.normalizeDate(dates[0]) === this.normalizeDate(new Date().setDate(1))) {
        this.selectedTimeRange = 'currentMonth';
      } else {
        this.selectedTimeRange = type;
      }
    },
    prevTimeRange() {
      if (this.selectedTimeRange === 'tomorrow') {
        this.selectedTimeRange = 'today';
      } else if (this.selectedTimeRange === 'today') {
        this.selectedTimeRange = 'day';
      }
      if (this.selectedTimeRange === 'day') {
        if (this.normalizeDate(this.currentTimeRange[0]) === this.normalizeDate(new Date().addDays(2))) {
          this.selectedTimeRange = 'tomorrow';
        } else {
          this.currentTimeRange = [
            new Date(this.currentTimeRange[0]).addDays(-1).format('Y-m-d'),
            new Date(this.currentTimeRange[0]).addDays(-1).format('Y-m-d'),
          ];
          [this.pickedTimeRange] = this.currentTimeRange;
          this.updateDelay.start(() => {
            this.update();
          });
        }
      }

      if (this.selectedTimeRange === 'currentMonth') {
        this.selectedTimeRange = 'lastMonth';
      } else if (this.selectedTimeRange === 'lastMonth') {
        this.selectedTimeRange = 'month';
      }
      if (this.selectedTimeRange === 'month') {
        if (this.normalizeDate(new Date(this.currentTimeRange[0]).setDate(1)) === this.normalizeDate(new Date().addMonths(1).setDate(1))) {
          this.selectedTimeRange = 'currentMonth';
        } else {
          this.currentTimeRange = [
            new Date(this.currentTimeRange[0]).addMonths(-1).format('Y-m-d'),
            new Date(new Date(this.currentTimeRange[0]).setDate(1)).addDays(-1).format('Y-m-d'),
          ];
          [this.pickedTimeRange] = this.currentTimeRange;
          this.updateDelay.start(() => {
            this.update();
          });
        }
      }
    },
    nextTimeRange() {
      if (this.selectedTimeRange === 'today') {
        this.selectedTimeRange = 'tomorrow';
      } else if (this.selectedTimeRange === 'tomorrow') {
        this.selectedTimeRange = 'day';
      }
      if (this.selectedTimeRange === 'day') {
        if (this.normalizeDate(this.currentTimeRange[0]) === this.normalizeDate(new Date().addDays(-1))) {
          this.selectedTimeRange = 'today';
        } else {
          this.currentTimeRange = [
            new Date(this.currentTimeRange[0]).addDays(1).format('Y-m-d'),
            new Date(this.currentTimeRange[0]).addDays(1).format('Y-m-d'),
          ];
          [this.pickedTimeRange] = this.currentTimeRange;
          this.updateDelay.start(() => {
            this.update();
          });
        }
      }

      if (this.selectedTimeRange === 'lastMonth') {
        this.selectedTimeRange = 'currentMonth';
      } else if (this.selectedTimeRange === 'currentMonth') {
        this.selectedTimeRange = 'month';
      }
      if (this.selectedTimeRange === 'month') {
        if (this.normalizeDate(new Date(this.currentTimeRange[0]).setDate(1)) === this.normalizeDate(new Date().addMonths(-2).setDate(1))) {
          this.selectedTimeRange = 'lastMonth';
        } else {
          this.currentTimeRange = [
            new Date(this.currentTimeRange[0]).addMonths(1).format('Y-m-d'),
            new Date(new Date(this.currentTimeRange[0]).addMonths(2).setDate(1)).addDays(-1)
              .format('Y-m-d'),
          ];
          [this.pickedTimeRange] = this.currentTimeRange;
          this.updateDelay.start(() => {
            this.update();
          });
        }
      }
    },
    setVariable(name, value) {
      this[name] = value;
    },
    update(emit = true) {
      if (this.isToolbarActive) {
        if (this.isResolutionActive) {
          this.currentParams.resolution = this.currentResolution;
        }
        if (this.isTimeRangeActive || this.currentTimeRange.length) {
          this.currentParams.timeRange = this.currentTimeRange;
        }
        this.currentParams.type = this.currentType;
      }

      this.setTooltip();

      if (this.endpoint) {
        this.handleChartUpdate(this.currentParams);
      }
      if (emit) {
        this.currentOptions = merge(this.currentOptions, {
          chart: {
            type: this.currentParams.type,
            toolbar: {
              timeRange: {
                value: this.currentParams.timeRange,
              },
              resolution: {
                value: this.currentParams.resolution,
              },
            },
          },
        });
        this.currentData = {
          ...this.currentOptions,
        };
        if (this.series.length) {
          this.currentData.series = this.series;
        }
        if (!this.endpoint) {
          this.canBeMounted = this.currentData.series.length;
        }
        this.isLoading = false;
        this.$emit('update', this.currentParams);
      }
    },
    setTooltip() {
      const that = this;

      this.currentOptions.tooltip = {
        ...this.currentOptions.tooltip,
        formatter(tooltip) {
          const options = {
            chart: that.chartClass,
            x: this.points.map((point) => Chart.getDateForResolution(
              point.x,
              tooltip.options.dateFormat ?? 'hour',
              {
                extremes: tooltip.chart.axes[0],
              },
            )),
            y: this.points.map((point) => that.formatYValue(point.y, {})),
            points: this.points.map((point) => ({
              seriesName: point.series.name,
            })),
          };

          // Create a new Vue instance to render the slot content
          const vm = new Vue({
            parent: that, // Inherit from the parent to access slots
            data() {
              return options;
            },
            render(h) {
              // Access the slot from the parent component
              const slot = this.$parent.$scopedSlots.tooltip;
              if (slot) {
                // Render the slot content with 'options' as the scope
                return slot(options);
              }
              // Render default content if no slot is provided
              return h('div', { class: 'base-chart-tooltip' }, [
                h('div', [
                  h('span', { class: 'base-chart-tooltip--date' }, options.x[0]),
                  h(
                    'span',
                    { class: 'base-chart-tooltip--value' },
                    ` ${options.points[0].seriesName}: ${options.y[0]} ${options.unit ?? ''}`,
                  ),
                ]),
              ]);
            },
          }).$mount();

          // Schedule the destruction of the Vue instance
          Vue.nextTick(() => {
            vm.$destroy();
          });

          return vm.$el.outerHTML;
        },
      };
    },
    getResolutions() {
      const resolution = this.currentOptions?.chart?.toolbar?.resolution;
      const resolutions = this.options?.chart?.toolbar?.resolution?.list || resolution?.list;
      const timerange = [
        new Date(this.currentTimeRange[0]),
        new Date(this.currentTimeRange[1]),
      ];

      return resolution?.filter?.(resolutions, timerange, {
        years: Chart.dateDiff(timerange[0], timerange[1], 'years'),
        months: Chart.dateDiff(timerange[0], timerange[1], 'months'),
        weeks: Chart.dateDiff(timerange[0], timerange[1], 'weeks'),
        days: Chart.dateDiff(timerange[0], timerange[1], 'days'),
      });
    },
    handleChartUpdate(event) {
      this.isLoading = true;
      axios.get(this.endpoint, {
        params: this.currentParams,
      })
        .then((response) => {
          const series = response.data;

          this.currentOptions = merge(this.currentOptions, {
            chart: {
              type: event.type,
              toolbar: {
                timeRange: {
                  value: event.timeRange,
                },
                resolution: {
                  value: event.resolution,
                },
              },
            },
          });
          this.currentData = {
            ...this.currentOptions,
            series,
          };

          if (Array.isArray(series)) {
            const allTimeSteps = series.map((el) => el.data.map(
              (seriesData) => seriesData[0],
            )).flat();
            this.setVariable('currentZoom', {
              min: Math.min(...allTimeSteps),
              max: Math.max(...allTimeSteps),
            });
          }

          this.isLoading = false;
          this.canBeMounted = true;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    formatYValue(value, options) {
      if (typeof options.formatYValue === 'function') {
        return options.formatYValue(point.y);
      }
      return value.toString().replace('.', ',');
    },
  },
};
</script>

<style lang="scss" scoped>
.base-chart {
  &--toolbar {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 1rem 1rem;

    .base-button {
      padding: 0.5rem;

      &:hover,
      &:focus,
      &--active {
        box-shadow: none !important;
        color: var(--company-main-primary);
      }
    }
  }

  &--time-range {
    display: grid;
    grid-template-columns: v-bind(timeRangeGridColumns);
    grid-template-areas: 'timeRangeLabel timeRangeSelect timeRangeDatePicker';
    align-items: center;
    gap: 0.75rem;

    @media (max-width: 830px) {
      grid-template-columns: auto auto;
      grid-template-areas: 'timeRangeLabel timeRangeSelect' 'timeRangeDatePicker timeRangeDatePicker';
    }
  }

  &--toolbar-item {
    display: flex;
    align-items: baseline;
    justify-content: flex-start;

    &:first-child {
      flex-wrap: wrap;
      gap: 1rem;
    }
  }

  &--toolbar-datepicker {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;

    :deep(.base-date-time-picker) {
      .input-field {
        text-align: center;
        color: var(--company-main-primary);
      }
    }
  }

  .highcharts-tooltip {
    .base-chart-tooltip {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      &--date {
        font-size: 0.75rem;
        font-weight: 300;
      }

      &--value {
        font-size: 1rem;
        font-weight: 500;
      }
    }
  }
}
</style>
